/**
 * NFR-8 — "No silent catches. Every error is recovered, wrapped with context, or surfaced, and
 * logged (OBS-1)."
 *
 * Typed categories, so a caller can decide *by category* rather than by string-matching a message.
 * Every one carries a `cause`, so wrapping never destroys the original stack.
 */

export type ErrorCategory =
  | 'config'
  | 'storage'
  | 'measurement'
  | 'environment'
  | 'export'
  | 'archive'
  | 'observability';

export class TouchlineError extends Error {
  override readonly name: string = 'TouchlineError';

  constructor(
    readonly category: ErrorCategory,
    message: string,
    options?: { cause?: unknown; context?: Record<string, unknown> },
  ) {
    super(message, options?.cause === undefined ? undefined : { cause: options.cause });
    this.context = options?.context ?? {};
  }

  readonly context: Record<string, unknown>;

  /** Flattened for the logger and for the exported JSON — `Error` does not serialise usefully. */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      category: this.category,
      message: this.message,
      context: this.context,
      cause: describeUnknownError(this.cause),
    };
  }
}

/** IndexedDB/Dexie failures: open, migrate, transaction abort, quota. */
export class StorageError extends TouchlineError {
  override readonly name = 'StorageError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('storage', message, options);
  }
}

/**
 * The bundle on this device is **older** than the schema in IndexedDB (Dexie `VersionError`).
 *
 * Its own class because design §13.5 gives it its own recovery screen: forward-only migrations make
 * a rollback across a version bump data-destructive (§13.4), so the correct response is to offer a
 * real BAK-1 export — which is possible because `archive/` has no `domain/` dependency — and tell
 * the user to update, rather than to crash or to "repair" the database. Folding it into a generic
 * open failure would put a data-loss path behind a message that reads like a transient error.
 */
export class SchemaVersionError extends TouchlineError {
  override readonly name = 'SchemaVersionError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('storage', message, options);
  }
}

/** A trial that did not produce a usable timing. Never averaged away (see `stats.summarize`). */
export class MeasurementError extends TouchlineError {
  override readonly name = 'MeasurementError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('measurement', message, options);
  }
}

/** A platform capability the harness needs is missing or refused. */
export class EnvironmentError extends TouchlineError {
  override readonly name = 'EnvironmentError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('environment', message, options);
  }
}

/**
 * An archive could not be built or read (build unit U-5).
 *
 * Its own category because the caller's response differs from an `ExportError`'s: this one says the
 * *bytes* are wrong or unrepresentable — a zip beyond the format's 32-bit limits, a scope naming a
 * match that is not in the source, a container that is not a STORE-only Touchline archive. It is
 * deliberately narrow: malformed **row data** never throws, because §13.5's recovery export has to
 * succeed on a profile that is already damaged. Those are reported in `ArchiveResult.report`
 * instead, so the export completes and the damage is still surfaced (NFR-8).
 */
export class ArchiveError extends TouchlineError {
  override readonly name = 'ArchiveError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('archive', message, options);
  }
}

/** Getting results off the device failed — the one thing a venue run cannot tolerate silently. */
export class ExportError extends TouchlineError {
  override readonly name = 'ExportError';
  constructor(message: string, options?: { cause?: unknown; context?: Record<string, unknown> }) {
    super('export', message, options);
  }
}

/**
 * Turns anything a `catch` can receive into something loggable and serialisable.
 * `catch (e)` binds `unknown`; throwing a string is legal JavaScript and libraries do it.
 */
export function describeUnknownError(err: unknown): Record<string, unknown> | null {
  if (err === undefined || err === null) return null;
  if (err instanceof TouchlineError) return err.toJSON();
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack ?? null,
      cause: err.cause === undefined ? null : describeUnknownError(err.cause),
    };
  }
  if (typeof err === 'object') {
    try {
      return { thrown: JSON.parse(JSON.stringify(err)) as unknown };
    } catch (serialisationFailure) {
      // Not a silent catch: the failure is reported in the value we return.
      return {
        thrown: String(err),
        note: `value was not JSON-serialisable: ${String(serialisationFailure)}`,
      };
    }
  }
  return { thrown: String(err) };
}

/** Short, human-readable form for on-screen surfacing at a venue. */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}
